import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { isNativeApp } from './platform';
import {
  getStoredSession,
  setStoredSession,
  type AuthSession,
  type AuthUser,
  userFromAccessToken,
} from './session';

export type OAuthProvider = 'google';

interface AuthContextValue {
  user: AuthUser | null;
  session: AuthSession | null;
  loading: boolean;
  signInWithGoogle: (redirectPath?: string) => void;
  signOut: () => Promise<void>;
  /** Called by AuthCallback after Google redirect. */
  establishSession: (accessToken: string) => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const API_BASE = (import.meta.env.VITE_API_URL as string) || '';

function apiRoot() {
  return API_BASE || '';
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<AuthSession | null>(() => getStoredSession());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setSession(getStoredSession());
    setLoading(false);
  }, []);

  const value: AuthContextValue = {
    user: session?.user ?? null,
    session,
    loading,
    signInWithGoogle: (redirectPath = '/dashboard') => {
      const next = encodeURIComponent(redirectPath);
      const native = isNativeApp ? '&native=1' : '';
      window.location.assign(`${apiRoot()}/api/auth/google?next=${next}${native}`);
    },
    signOut: async () => {
      setStoredSession(null);
      setSession(null);
      try {
        await fetch(`${apiRoot()}/api/auth/logout`, { method: 'POST' });
      } catch {
        /* ignore */
      }
    },
    establishSession: (accessToken) => {
      const user = userFromAccessToken(accessToken);
      if (!user) throw new Error('Invalid session token');
      const nextSession: AuthSession = { access_token: accessToken, user };
      setStoredSession(nextSession);
      setSession(nextSession);
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
